/**
 * GIWAHS Adaptive Journey Core Engine — JourneyConfig (AJIP-16.02.003 · WS8)
 * ---------------------------------------------------------------------
 * Configuration-driven journey definitions. New journey types are added
 * by supplying a valid definition — the Core Engine is never modified.
 *
 * Definition shape
 *   identifier          string           unique, non-empty
 *   name                string           display / audit name
 *   description         string           free-form description
 *   entryConditions     string[]         ordered list of condition ids
 *   exitConditions      string[]         ordered list of condition ids
 *   availableStates     string[]         subset of ALL_STATES
 *   transitions         Transition[]     deterministic rules
 *   enabledFeatures     string[]         opaque feature identifiers
 *   requiredPermissions string[]         opaque permission identifiers
 *
 * Transition shape
 *   from   string       origin state (must be in availableStates)
 *   event  string       symbolic event that triggers the transition
 *   to     string       target state (must be in availableStates)
 */
import { ALL_STATES, STATES } from './JourneyState';
import { ConfigError } from './JourneyErrors';

const REQUIRED_FIELDS = Object.freeze([
  'identifier',
  'name',
  'description',
  'entryConditions',
  'exitConditions',
  'availableStates',
  'transitions',
  'enabledFeatures',
  'requiredPermissions',
]);

const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;
const isStringArray = (v) => Array.isArray(v) && v.every((x) => typeof x === 'string');

const validateTransition = (t, availableStates, ctx) => {
  if (!t || typeof t !== 'object') {
    throw new ConfigError(`${ctx}: transition must be an object`, { transition: t });
  }
  if (!isNonEmptyString(t.from) || !isNonEmptyString(t.event) || !isNonEmptyString(t.to)) {
    throw new ConfigError(`${ctx}: transition requires non-empty from, event, to`, { transition: t });
  }
  if (!availableStates.includes(t.from)) {
    throw new ConfigError(`${ctx}: transition.from '${t.from}' not in availableStates`, { transition: t });
  }
  if (!availableStates.includes(t.to)) {
    throw new ConfigError(`${ctx}: transition.to '${t.to}' not in availableStates`, { transition: t });
  }
};

const validateJourneyDefinition = (def) => {
  if (!def || typeof def !== 'object') {
    throw new ConfigError('Journey definition must be a non-null object');
  }
  for (const field of REQUIRED_FIELDS) {
    if (!(field in def)) {
      throw new ConfigError(`Journey definition missing required field: ${field}`, { field });
    }
  }
  const ctx = `Journey '${def.identifier}'`;
  if (!isNonEmptyString(def.identifier)) {
    throw new ConfigError('identifier must be a non-empty string', { def });
  }
  if (!isNonEmptyString(def.name)) {
    throw new ConfigError(`${ctx}: name must be a non-empty string`);
  }
  if (typeof def.description !== 'string') {
    throw new ConfigError(`${ctx}: description must be a string`);
  }
  if (!isStringArray(def.entryConditions)) {
    throw new ConfigError(`${ctx}: entryConditions must be string[]`);
  }
  if (!isStringArray(def.exitConditions)) {
    throw new ConfigError(`${ctx}: exitConditions must be string[]`);
  }
  if (!isStringArray(def.availableStates) || def.availableStates.length === 0) {
    throw new ConfigError(`${ctx}: availableStates must be a non-empty string[]`);
  }
  for (const s of def.availableStates) {
    if (!ALL_STATES.includes(s)) {
      throw new ConfigError(`${ctx}: availableStates contains unknown state '${s}'`);
    }
  }
  if (!def.availableStates.includes(STATES.NOT_STARTED)) {
    throw new ConfigError(`${ctx}: availableStates must include NOT_STARTED`);
  }
  if (!Array.isArray(def.transitions)) {
    throw new ConfigError(`${ctx}: transitions must be an array`);
  }
  for (const t of def.transitions) validateTransition(t, def.availableStates, ctx);
  if (!isStringArray(def.enabledFeatures)) {
    throw new ConfigError(`${ctx}: enabledFeatures must be string[]`);
  }
  if (!isStringArray(def.requiredPermissions)) {
    throw new ConfigError(`${ctx}: requiredPermissions must be string[]`);
  }
  return true;
};

const freezeDefinition = (def) => {
  validateJourneyDefinition(def);
  const frozen = {
    identifier: def.identifier,
    name: def.name,
    description: def.description,
    entryConditions: Object.freeze([...def.entryConditions]),
    exitConditions: Object.freeze([...def.exitConditions]),
    availableStates: Object.freeze([...def.availableStates]),
    transitions: Object.freeze(def.transitions.map((t) => Object.freeze({ from: t.from, event: t.event, to: t.to }))),
    enabledFeatures: Object.freeze([...def.enabledFeatures]),
    requiredPermissions: Object.freeze([...def.requiredPermissions]),
  };
  return Object.freeze(frozen);
};

export { REQUIRED_FIELDS, validateJourneyDefinition, freezeDefinition };
